import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Camera, X, Check, Loader2, Upload, ArrowLeft, ChevronRight, RotateCcw, Video } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { kycApiService } from '@/services/kycApi'
import { useAuthStore } from '@/stores/authStore'
import toast from 'react-hot-toast'

interface KYCWelcomeModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

type DocumentType = 'id_card' | 'passport' | 'proof_of_address'

type WelcomeStep = 'step1' | 'step2' | 'step3' | 'document-type' | 'capture' | 'video' | 'uploading'

const welcomeSteps: WelcomeStep[] = ['step1', 'step2', 'step3']

export const KYCWelcomeModal: React.FC<KYCWelcomeModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const { t } = useTranslation()
  const { updateUser } = useAuthStore()
  const [currentStep, setCurrentStep] = useState<WelcomeStep>('step1')
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null)
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [frontFile, setFrontFile] = useState<File | null>(null)
  const [backFile, setBackFile] = useState<File | null>(null)
  const [movementVideoFile, setMovementVideoFile] = useState<File | null>(null)
  const [movementVideoUrl, setMovementVideoUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isCameraActive, setIsCameraActive] = useState(false)
  const [captureStep, setCaptureStep] = useState<'front' | 'back' | 'complete'>('front')
  const [capturedImages, setCapturedImages] = useState<{ front?: string; back?: string }>({})
  const [showFlipAnimation, setShowFlipAnimation] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  
  const documentVideoRef = useRef<HTMLVideoElement>(null)
  const movementVideoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const isCameraStartingRef = useRef(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const recordingChunksRef = useRef<Blob[]>([])
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null)

  const getDocumentTypeName = (type: DocumentType): string => {
    switch (type) {
      case 'id_card':
        return t('kyc.idCard', 'Bilhete de Identidade')
      case 'passport':
        return t('kyc.passport', 'Passaporte')
      case 'proof_of_address':
        return t('kyc.proofOfResidence', 'Comprovativo de Residência')
    }
  }

  const needsBothSides = (type: DocumentType | null) => {
    return type === 'id_card'
  }

  const getStepContent = (step: WelcomeStep) => {
    switch (step) {
      case 'step1':
        return {
          image: '/assets/image33.png',
          title: t('kyc.sendMoney', 'Enviar dinheiro'),
          description: 'A djelo te permite enviar dinheiro para outras pessoas através da djelo'
        }
      case 'step2':
        return {
          image: '/assets/image34.png',
          title: t('kyc.qrPayment', 'Pagamento QR Code'),
          description: 'Podes pagar nos comerciantes locais aderentes'
        }
      case 'step3':
        return {
          image: '/assets/image1323.png',
          title: t('kyc.documentPhotoTitle', 'Document photo required'),
          description: t(
            'kyc.documentPhotoDescription',
            'We will need a clear photo of your identification document before continuing.'
          )
        }
      default:
        return null
    }
  }

  const getCurrentStepIndex = () => {
    if (welcomeSteps.includes(currentStep as WelcomeStep)) {
      return welcomeSteps.indexOf(currentStep as WelcomeStep)
    }
    return -1
  }

  const handleNext = () => {
    const currentIndex = getCurrentStepIndex()
    if (currentIndex < welcomeSteps.length - 1) {
      setCurrentStep(welcomeSteps[currentIndex + 1])
    } else if (currentStep === 'step3') {
      setCurrentStep('document-type')
    }
  }

  const handleBack = () => {
    const currentIndex = getCurrentStepIndex()
    if (currentIndex > 0) {
      setCurrentStep(welcomeSteps[currentIndex - 1])
    } else if (currentStep === 'document-type') {
      setCurrentStep('step3')
    } else if (currentStep === 'capture') {
      setCurrentStep('document-type')
    }
  }

  const startCamera = useCallback(async (facingMode: 'user' | 'environment', targetRef: React.RefObject<HTMLVideoElement>) => {
    if (isCameraStartingRef.current) {
      return
    }
    
    if (streamRef.current && streamRef.current.active) {
      return
    }
    
    try {
      isCameraStartingRef.current = true
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      })
      
      streamRef.current = mediaStream

      const attachStream = async () => {
        if (!targetRef.current) {
          return false
        }

        targetRef.current.srcObject = mediaStream
        setIsCameraActive(true)

        try {
          await targetRef.current.play()
        } catch (playError) {
          // Ignore play errors
        }
        return true
      }

      if (!(await attachStream())) {
        let attempts = 0
        const interval = setInterval(async () => {
          attempts += 1
          const attached = await attachStream()
          if (attached || attempts >= 10) {
            clearInterval(interval)
            if (!attached) {
              mediaStream.getTracks().forEach(track => track.stop())
              streamRef.current = null
              setIsCameraActive(false)
            }
          }
        }, 100)
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error)
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error(t('kyc.cameraPermissionDenied', 'Permissão da câmera negada. Por favor, permita o acesso à câmera nas configurações.'))
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error(t('kyc.cameraNotFound', 'Câmera não encontrada. Por favor, verifique se o dispositivo tem uma câmera disponível.'))
      } else {
        toast.error(t('kyc.cameraError', 'Erro ao acessar a câmera'))
      }
    } finally {
      isCameraStartingRef.current = false
    }
  }, [t])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop()
      })
      streamRef.current = null
    }
    if (documentVideoRef.current) {
      documentVideoRef.current.srcObject = null
      documentVideoRef.current.pause()
    }
    if (movementVideoRef.current) {
      movementVideoRef.current.srcObject = null
      movementVideoRef.current.pause()
    }
    setIsCameraActive(false)
    isCameraStartingRef.current = false
  }, [])

  const startDocumentCamera = useCallback(async () => {
    stopCamera()
    await startCamera('environment', documentVideoRef)
  }, [startCamera, stopCamera])

  const startMovementCamera = useCallback(async () => {
    stopCamera()
    await startCamera('user', movementVideoRef)
  }, [startCamera, stopCamera])

  const capturePhoto = useCallback(() => {
    if (!documentVideoRef.current || !canvasRef.current || !selectedDocumentType) {
      return
    }

    const video = documentVideoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    if (!context) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    canvas.toBlob((blob) => {
      if (!blob) return
      const imageUrl = URL.createObjectURL(blob)
      const file = new File([blob], `kyc-document-${Date.now()}.jpg`, { type: 'image/jpeg' })

      if (needsBothSides(selectedDocumentType) && captureStep === 'front') {
        setCapturedImages((prev) => ({ ...prev, front: imageUrl }))
        setFrontFile(file)
        setCaptureStep('back')
        setShowFlipAnimation(true)
        setTimeout(() => setShowFlipAnimation(false), 4000)
        return
      }

      if (needsBothSides(selectedDocumentType)) {
        setCapturedImages((prev) => ({ ...prev, back: imageUrl }))
        setBackFile(file)
      } else {
        setFrontFile(file)
      }

      setCapturedImage(imageUrl)
      setCaptureStep('complete')
      stopCamera()
    }, 'image/jpeg', 0.85)
  }, [selectedDocumentType, captureStep, stopCamera, needsBothSides])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop()
      } catch (error) {
        console.error('Error stopping recording:', error)
      }
    }
    setIsRecording(false)
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current)
      recordingTimerRef.current = null
    }
  }, [])

  const startRecording = useCallback(() => {
    if (!streamRef.current) {
      toast.error(t('kyc.cameraNotReady', 'Camera not ready'))
      return
    }

    try {
      recordingChunksRef.current = []

      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: 'video/webm;codecs=vp8,opus'
      })

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' })
        const file = new File([blob], `kyc-video-${Date.now()}.webm`, { type: 'video/webm' })
        const videoUrl = URL.createObjectURL(blob)
        setMovementVideoFile(file)
        setMovementVideoUrl(videoUrl)
        setIsRecording(false)
        stopCamera()
      }

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event)
        setIsRecording(false)
        toast.error(t('kyc.recordingError', 'Recording error'))
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 0.1
          if (newTime >= 5) {
            stopRecording()
            return 5
          }
          return newTime
        })
      }, 100)
    } catch (error) {
      console.error('Error starting recording:', error)
      setIsRecording(false)
      toast.error(t('kyc.recordingError', 'Recording error'))
    }
  }, [stopCamera, stopRecording, t])

  React.useEffect(() => {
    if (!isOpen) {
      stopCamera()
      stopRecording()
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current)
        recordingTimerRef.current = null
      }
    }
  }, [isOpen, stopCamera, stopRecording])

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file)
      if (needsBothSides(selectedDocumentType) && captureStep === 'front') {
        setCapturedImages((prev) => ({ ...prev, front: imageUrl }))
        setFrontFile(file)
        setCaptureStep('back')
        setShowFlipAnimation(true)
        setTimeout(() => setShowFlipAnimation(false), 4000)
      } else {
        if (needsBothSides(selectedDocumentType)) {
          setCapturedImages((prev) => ({ ...prev, back: imageUrl }))
          setBackFile(file)
        } else {
          setFrontFile(file)
        }
        setCapturedImage(imageUrl)
        setCaptureStep('complete')
      }
    } else {
      toast.error(t('kyc.imageOnly', 'Please select an image file'))
    }
  }, [t, captureStep, selectedDocumentType, needsBothSides])

  const handleClose = useCallback(() => {
    stopCamera()
    stopRecording()
    setCurrentStep('step1')
    setSelectedDocumentType(null)
    setCapturedImage(null)
    setFrontFile(null)
    setBackFile(null)
    setMovementVideoFile(null)
    setMovementVideoUrl(null)
    setIsCameraActive(false)
    setCaptureStep('front')
    setCapturedImages({})
    setShowFlipAnimation(false)
    setIsRecording(false)
    setRecordingTime(0)
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage)
    }
    if (movementVideoUrl) {
      URL.revokeObjectURL(movementVideoUrl)
    }
    Object.values(capturedImages).forEach((url) => {
      if (url) URL.revokeObjectURL(url)
    })
    onClose()
  }, [stopCamera, stopRecording, capturedImage, capturedImages, movementVideoUrl, onClose])

  const handleUpload = useCallback(async () => {
    if (!selectedDocumentType) return
    if (!frontFile) {
      toast.error(t('kyc.documentRequired', 'Please capture the document first'))
      return
    }
    if (needsBothSides(selectedDocumentType) && !backFile) {
      toast.error(t('kyc.backRequired', 'Please capture the back side of the document'))
      return
    }
    if (!movementVideoFile) {
      toast.error(t('kyc.videoRequired', 'Please record the verification video'))
      return
    }

    setIsUploading(true)
    setCurrentStep('uploading')

    try {
      const docKey =
        selectedDocumentType === 'id_card'
          ? 'bi'
          : selectedDocumentType === 'passport'
            ? 'passport'
            : 'proof_of_address'

      const documents: Record<string, File> = {}
      if (needsBothSides(selectedDocumentType)) {
        if (frontFile) documents[`${docKey}_front_document`] = frontFile
        if (backFile) documents[`${docKey}_back_document`] = backFile
      } else if (frontFile) {
        documents[docKey] = frontFile
      }

      const result = await kycApiService.submitKYCData(
        documents,
        undefined,
        movementVideoFile
      )

      if (result.success) {
        toast.success(t('kyc.uploadSuccess', 'KYC submitted successfully!'))
        updateUser({ kyc_status: 'pending' })
        onComplete()
        setTimeout(() => {
          handleClose()
        }, 1000)
      } else {
        throw new Error(result.error || 'Upload failed')
      }
    } catch (error: any) {
      console.error('KYC upload error:', error)
      const errorMessage = error.message || t('kyc.uploadError', 'Error uploading KYC data')

      toast.error(errorMessage)
      // Don't reset to step1, stay on capture step so user can retry
      setCurrentStep(movementVideoFile ? 'video' : 'capture')
    } finally {
      setIsUploading(false)
    }
  }, [selectedDocumentType, frontFile, backFile, movementVideoFile, t, updateUser, onComplete, handleClose, needsBothSides])

  const handleDocumentTypeSelect = (type: DocumentType) => {
    setSelectedDocumentType(type)
    setCapturedImage(null)
    setFrontFile(null)
    setBackFile(null)
    setMovementVideoFile(null)
    setMovementVideoUrl(null)
    setCaptureStep('front')
    setCapturedImages({})
    setShowFlipAnimation(false)
    setIsRecording(false)
    setRecordingTime(0)
    setCurrentStep('capture')
  }

  React.useEffect(() => {
    if (currentStep === 'capture' && !capturedImage) {
      const timer = setTimeout(() => {
        if (!streamRef.current) {
          startDocumentCamera()
        }
      }, 100)
      return () => {
        clearTimeout(timer)
      }
    }

    if (currentStep === 'video' && !movementVideoFile) {
      const timer = setTimeout(() => {
        if (!streamRef.current) {
          startMovementCamera()
        }
      }, 100)
      return () => {
        clearTimeout(timer)
      }
    }

    if (currentStep !== 'capture' && currentStep !== 'video') {
      stopCamera()
    }

    return () => {
      if (currentStep !== 'capture' && currentStep !== 'video') {
        stopCamera()
      }
    }
  }, [currentStep, capturedImage, movementVideoFile, startDocumentCamera, startMovementCamera, stopCamera])

  const stepContent = getStepContent(currentStep)
  const currentStepIndex = getCurrentStepIndex()
  const isWelcomeStep = welcomeSteps.includes(currentStep as WelcomeStep)

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="kyc-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget && currentStep !== 'uploading') {
              handleClose()
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {currentStep === 'step1' && t('kyc.welcome', 'Bem-vindo ao Djelo!')}
                  {currentStep === 'step2' && t('kyc.qrPayment', 'Pagamento QR Code')}
                  {currentStep === 'step3' && t('kyc.documentPhotoTitle', 'Document photo required')}
                  {currentStep === 'document-type' && t('kyc.selectDocument', 'Select the document')}
                  {currentStep === 'capture' && t('kyc.captureDocumentTitle', 'Capture your document')}
                  {currentStep === 'video' && t('kyc.recordVideoTitle', 'Record verification video')}
                  {currentStep === 'uploading' && t('kyc.uploading', 'Uploading...')}
                </h2>
                {currentStep !== 'uploading' && (
                  <button
                    onClick={handleClose}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AnimatePresence mode="wait">
                {isWelcomeStep && stepContent && (
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center space-y-6"
                  >
                    <div className="w-full rounded-2xl overflow-hidden shadow-lg">
                      <img
                        src={stepContent.image}
                        alt={stepContent.title}
                        className="w-full h-auto object-cover"
                      />
                    </div>

                    <h3 className="text-2xl font-bold text-text-primary text-center">
                      {stepContent.title}
                    </h3>

                    <p className="text-text-secondary text-center text-lg leading-relaxed">
                      {stepContent.description}
                    </p>
                  </motion.div>
                )}

                {currentStep === 'document-type' && (
                  <motion.div
                    key="document-type"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-4"
                  >
                    <p className="text-text-secondary text-center mb-6 text-lg">
                      {t('kyc.selectDocumentType', 'Que tipo de documento deseja enviar?')}
                    </p>

                    <button
                      onClick={() => handleDocumentTypeSelect('id_card')}
                      className="w-full p-5 border-2 border-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left group"
                    >
                      <div className="font-semibold text-text-primary text-lg group-hover:text-primary">
                        {getDocumentTypeName('id_card')}
                      </div>
                    </button>

                    <button
                      onClick={() => handleDocumentTypeSelect('passport')}
                      className="w-full p-5 border-2 border-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left group"
                    >
                      <div className="font-semibold text-text-primary text-lg group-hover:text-primary">
                        {getDocumentTypeName('passport')}
                      </div>
                    </button>

                    <button
                      onClick={() => handleDocumentTypeSelect('proof_of_address')}
                      className="w-full p-5 border-2 border-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left group"
                    >
                      <div className="font-semibold text-text-primary text-lg group-hover:text-primary">
                        {getDocumentTypeName('proof_of_address')}
                      </div>
                    </button>
                  </motion.div>
                )}

                {currentStep === 'capture' && (
                  <motion.div
                    key="capture"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-4"
                  >
                    {!capturedImage ? (
                      <>
                        {/* User camera preview */}
                        <div className="relative bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden aspect-video">
                          <video
                            ref={documentVideoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Video error:', e)
                            }}
                          />
                          <canvas ref={canvasRef} className="hidden" />

                          {!isCameraActive && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 z-10">
                              <Camera className="w-12 h-12 text-gray-400 mb-4 animate-pulse" />
                              <p className="text-sm text-gray-500 mb-4">
                                {t('kyc.startingCamera', 'Camera access is required to take the photo')}
                              </p>
                              <Button
                                onClick={() => startDocumentCamera()}
                                className="mt-2"
                              >
                                {t('kyc.allowCamera', 'Allow camera access')}
                              </Button>
                            </div>
                          )}

                          {/* Document frame */}
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="border-2 border-primary border-dashed rounded-lg bg-transparent w-[80%] aspect-[1.586] max-w-[280px]">
                              <div className="absolute -top-8 left-0 text-xs text-primary font-medium bg-white dark:bg-gray-800 px-2 py-1 rounded">
                                {t('kyc.alignDocument', 'Align the document inside the frame')}
                              </div>
                            </div>
                          </div>

                          {/* Flip animation */}
                          {showFlipAnimation && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/40">
                              <div className="text-center text-white">
                                <div className="relative w-32 h-20 mx-auto mb-4">
                                  <div
                                    className="relative w-full h-full"
                                    style={{ perspective: '1000px', transformStyle: 'preserve-3d' }}
                                  >
                                    <div
                                      className="relative w-full h-full"
                                      style={{
                                        transformStyle: 'preserve-3d',
                                        animation: 'flipHorizontal 2s ease-in-out'
                                      }}
                                    >
                                      <div
                                        className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg flex items-center justify-center border-2 border-white"
                                        style={{ backfaceVisibility: 'hidden', transform: 'rotateY(0deg)' }}
                                      >
                                        <span className="text-white text-lg font-bold">FRONT</span>
                                      </div>
                                      <div
                                        className="absolute inset-0 w-full h-full bg-gradient-to-r from-gray-400 to-gray-500 rounded-lg shadow-lg flex items-center justify-center border-2 border-white"
                                        style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                                      >
                                        <span className="text-white text-lg font-bold">BACK</span>
                                      </div>
                                    </div>
                                  </div>
                                  <style>{`
                                    @keyframes flipHorizontal {
                                      0% { transform: rotateY(0deg); }
                                      50% { transform: rotateY(90deg); }
                                      100% { transform: rotateY(180deg); }
                                    }
                                  `}</style>
                                </div>
                                <div className="bg-white/90 text-gray-800 px-4 py-2 rounded-lg">
                                  <p className="text-sm font-medium">
                                    {t('kyc.flipCardMessage', 'Flip the document to capture the back side')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {needsBothSides(selectedDocumentType) && (
                          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-text-primary">
                                {t('kyc.progress', 'Progress')}
                              </span>
                              <span className="text-xs text-text-secondary">
                                {captureStep === 'front' ? '1/2' : '2/2'}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: captureStep === 'front' ? '50%' : '100%' }}
                              />
                            </div>
                            <p className="text-xs text-text-secondary mt-1">
                              {captureStep === 'front'
                                ? t('kyc.captureFrontFirst', 'Capture the front side first')
                                : t('kyc.captureBackNow', 'Now capture the back side')}
                            </p>
                          </div>
                        )}

                        <p className="text-center text-text-secondary">
                          {needsBothSides(selectedDocumentType) && captureStep === 'back'
                            ? t('kyc.captureBackSide', 'Capture the back side of your document')
                            : t('kyc.captureFrontSide', 'Capture a clear photo of the document')}
                        </p>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              stopCamera()
                              setCurrentStep('document-type')
                            }}
                            className="flex-1"
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {t('common.back', 'Back')}
                          </Button>
                          <Button
                            onClick={capturePhoto}
                            className="flex-1"
                            disabled={!isCameraActive}
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            {captureStep === 'back'
                              ? t('kyc.captureBack', 'Capture back')
                              : t('kyc.captureFront', 'Capture front')}
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex-1"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {t('kyc.upload', 'Upload')}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="relative bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden">
                          <img
                            src={capturedImage}
                            alt={t('kyc.documentPreview', 'Document preview')}
                            className="w-full h-auto object-cover"
                          />
                        </div>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              if (capturedImage) {
                                URL.revokeObjectURL(capturedImage)
                              }
                              Object.values(capturedImages).forEach((url) => {
                                if (url) URL.revokeObjectURL(url)
                              })
                              setCapturedImage(null)
                              setFrontFile(null)
                              setBackFile(null)
                              setCapturedImages({})
                              setCaptureStep('front')
                              setIsCameraActive(false)
                              setTimeout(() => {
                                startDocumentCamera()
                              }, 100)
                            }}
                            className="flex-1"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            {t('common.retry', 'Try again')}
                          </Button>
                          <Button
                            onClick={() => setCurrentStep('video')}
                            className="flex-1"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            {t('common.next', 'Next')}
                          </Button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}

                {currentStep === 'video' && (
                  <motion.div
                    key="video"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-4"
                  >
                    {!movementVideoUrl ? (
                      <>
                        <div className="relative bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden aspect-video mb-4">
                          <video
                            src="/assets/kyc.mp4"
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <p className="text-center text-text-secondary">
                          {t('kyc.videoInstruction', 'Record a 5 second video holding your document')}
                        </p>

                        <div className="relative bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden aspect-video">
                          <video
                            ref={movementVideoRef}
                            autoPlay
                            playsInline
                            muted
                            className="w-full h-full object-cover"
                            style={{ transform: 'scaleX(-1)' }}
                          />
                          {!isCameraActive && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 z-10">
                              <Camera className="w-12 h-12 text-gray-400 mb-4 animate-pulse" />
                              <p className="text-sm text-gray-500 mb-4">
                                {t('kyc.startingCamera', 'Camera access is required to record the video')}
                              </p>
                              <Button
                                onClick={() => startMovementCamera()}
                                className="mt-2"
                              >
                                {t('kyc.allowCamera', 'Allow camera access')}
                              </Button>
                            </div>
                          )}
                          {isRecording && (
                            <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                              <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                              {Math.ceil(5 - recordingTime)}s
                            </div>
                          )}
                        </div>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              stopCamera()
                              setCurrentStep('capture')
                            }}
                            className="flex-1"
                          >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            {t('common.back', 'Back')}
                          </Button>
                          {!isRecording ? (
                            <Button
                              onClick={startRecording}
                              className="flex-1"
                              disabled={!isCameraActive}
                            >
                              <Video className="w-4 h-4 mr-2" />
                              {t('kyc.record', 'Record')}
                            </Button>
                          ) : (
                            <Button
                              onClick={stopRecording}
                              className="flex-1 bg-red-500 hover:bg-red-600"
                            >
                              {t('kyc.stop', 'Stop')}
                            </Button>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="video/*"
                            onChange={(event) => {
                              const file = event.target.files?.[0]
                              if (file && file.type.startsWith('video/')) {
                                const videoUrl = URL.createObjectURL(file)
                                setMovementVideoFile(file)
                                setMovementVideoUrl(videoUrl)
                              } else {
                                toast.error(t('kyc.videoOnly', 'Please select a video file'))
                              }
                            }}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            className="flex-1"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {t('kyc.upload', 'Upload')}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="relative bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden">
                          <video
                            src={movementVideoUrl}
                            controls
                            className="w-full"
                          />
                        </div>

                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            onClick={() => {
                              if (movementVideoUrl) {
                                URL.revokeObjectURL(movementVideoUrl)
                              }
                              setMovementVideoUrl(null)
                              setMovementVideoFile(null)
                              setIsCameraActive(false)
                              setRecordingTime(0)
                              setTimeout(() => {
                                startMovementCamera()
                              }, 100)
                            }}
                            className="flex-1"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            {t('common.retry', 'Try again')}
                          </Button>
                          <Button
                            onClick={handleUpload}
                            className="flex-1"
                            disabled={isUploading}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {t('common.uploading', 'Uploading...')}
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                {t('common.confirm', 'Confirm')}
                              </>
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </motion.div>
                )}

                {currentStep === 'uploading' && (
                  <motion.div
                    key="uploading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center py-12 space-y-4"
                  >
                    <Loader2 className="w-16 h-16 animate-spin text-primary" />
                    <p className="text-text-secondary text-center text-lg">
                      {t('kyc.uploadingDocument', 'Uploading your document...')}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer with Navigation */}
            {isWelcomeStep && (
              <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex gap-3">
                  {currentStepIndex > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {t('common.back', 'Voltar')}
                    </Button>
                  )}
                  <Button
                    onClick={handleNext}
                    className={`flex-1 ${currentStepIndex === 0 ? 'w-full' : ''}`}
                  >
                    {currentStepIndex === welcomeSteps.length - 1
                      ? t('common.start', 'Começar')
                      : t('common.next', 'Próximo')}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
